import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { database, auth } from './firebase';
import { ref, onValue, push, set, remove } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { uploadImage } from './cloudinary';
// import { FaTrash, FaEdit, FaPlus, FaArrowLeft } from 'react-icons/fa';
import AdminNavbar from './components/AdminNavbar';

const Container = styled.div`
  max-width: 1200px;
  margin: 5rem auto;
  padding: 1rem;
`;

const Title = styled.h2`
  font-size: 2rem;
  margin-bottom: 2rem;
  color: #333;
  margin-left: 1rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
  background: white;
  padding: 2rem 1rem;
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
`;

const FormHeading = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #333;
  text-align: center;
  font-weight: 400;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  min-height: 100px;
`;

const FileInput = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  width: 100%;
`;

const Button = styled.button`
  padding: 0.75rem;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    background: #0056b3;
  }
`;

const DeleteButton = styled(Button)`
  background: #dc3545;
  &:hover {
    background: #c82333;
  }
`;

const AchievementsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const AchievementCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
`;

// const AdminNav = styled.nav`
//   display: flex;
//   gap: 1rem;
//   margin-bottom: 2rem;
//   flex-wrap: wrap;
// `;

const AdminNavLink = styled.button`
  padding: 0.75rem 1.5rem;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1rem;
  text-decoration: none;
  transition: background 0.3s ease;

  &:hover {
    background: #0056b3;
  }
`;

// const LogoutButton = styled(AdminNavLink)`
//   background: #dc3545;
//   &:hover {
//     background: #c82333;
//   }
// `;

const Achievements = () => {
  const [achievements, setAchievements] = useState([]);
  // const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // const [editingAchievement, setEditingAchievement] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    imageUrl: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    const achievementsRef = ref(database, 'achievements');
    onValue(achievementsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const achievementsList = Object.entries(data).map(([id, achievement]) => ({
          id,
          ...achievement
        }));
        setAchievements(achievementsList);
      }
    });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFormData(prev => ({
        ...prev,
        imageUrl: e.target.files[0]
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.imageUrl) {
      alert('Please select an image');
      return;
    }

    try {
      // Upload image to Cloudinary
      const imageUrl = await uploadImage(formData.imageUrl);

      // Save achievement data with image URL
      const achievementsRef = ref(database, 'achievements');
      const newAchievementRef = push(achievementsRef);
      await set(newAchievementRef, {
        title: formData.title,
        description: formData.description,
        date: formData.date,
        imageUrl: imageUrl,
        createdAt: new Date().toISOString()
      });

      // Reset form
      setFormData({
        title: '',
        description: '',
        date: '',
        imageUrl: null
      });
    } catch (error) {
      console.error('Error adding achievement:', error);
      alert('Error adding achievement. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    try {
      const achievementRef = ref(database, `achievements/${id}`);
      await remove(achievementRef);
    } catch (error) {
      console.error('Error deleting achievement:', error);
    }
  };

  // const handleLogout = async () => {
  //   try {
  //     await signOut(auth);
  //     navigate('/admin');
  //   } catch (error) {
  //     console.error('Error logging out:', error);
  //   }
  // };

  return (
    <Container>
      <AdminNavbar />
      <Title>Achievements</Title>
      <Form onSubmit={handleSubmit}>
        <FormHeading>Add New Achievement</FormHeading>
        <Input
          type="text"
          name="title"
          placeholder="Title"
          value={formData.title}
          onChange={handleChange}
          required
        />
        <TextArea
          name="description"
          placeholder="Description"
          value={formData.description}
          onChange={handleChange}
          required
        />
        <Input
          type="date"
          name="date"
          value={formData.date}
          onChange={handleChange}
          required
        />
        <FileInput
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          required
        />
        <Button type="submit">
          {isModalOpen ? 'Uploading...' : 'Add Achievement'}
        </Button>
      </Form>
      <AchievementsGrid>
        {achievements.map(achievement => (
          <AchievementCard key={achievement.id}>
            <h3>{achievement.title}</h3>
            <p><strong>Date:</strong> {new Date(achievement.date).toLocaleDateString()}</p>
            <p><strong>Description:</strong> {achievement.description}</p>
            {achievement.imageUrl && <img src={achievement.imageUrl} alt={achievement.title} style={{ width: '100%', height: '200px', objectFit: 'cover' }} />}
            <DeleteButton onClick={() => handleDelete(achievement.id)}>Delete</DeleteButton>
          </AchievementCard>
        ))}
      </AchievementsGrid>
    </Container>
  );
};

export default Achievements; 